import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { preparePortalLogin } from '../mobile/accountSession';
import { flushSessionVault } from '../mobile/sessionVault';

const API_URL = process.env.REACT_APP_API_URL;

export default function SetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => (
    location.state?.email
    || searchParams.get('email')
    || sessionStorage.getItem('pendingClientEmail')
    || ''
  ).trim().toLowerCase());
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !password || !confirmPassword) {
      setError('Please enter your email and fill out both password fields.');
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/client/register/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail, password }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(data.error || 'Something went wrong while creating your password.');
        return;
      }
      if (!data.access || !data.case_id) {
        setError('Your password was created, but your portal session could not be started. Please log in.');
        return;
      }

      await preparePortalLogin('client');
      localStorage.setItem('clientToken', data.access);
      if (data.refresh) localStorage.setItem('clientRefreshToken', data.refresh);
      localStorage.setItem('clientCaseId', data.case_id);
      localStorage.setItem('clientEmail', data.email || normalizedEmail);
      await flushSessionVault();
      sessionStorage.removeItem('pendingClientEmail');
      navigate('/client/dashboard', { replace: true });
    } catch (requestError) {
      console.error(requestError);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 px-4">
      <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md">
        <img
          src="https://cisowskilaw.com/wp-content/uploads/2020/05/cropped-output-onlinepngtools-1-copy-63x63.png"
          alt="Cisowski Law"
          className="mx-auto h-12 w-12 mb-4"
        />
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
          Create Your Password
        </h2>
        <p className="text-sm text-gray-600 text-center mb-6">
          Create a password and you’ll be signed directly into your client portal.
        </p>

        {error && <p className="text-red-600 text-sm mb-4 text-center">{error}</p>}

        <form onSubmit={handleSubmit} autoComplete="on" className="space-y-4">
          <div>
            <label htmlFor="client-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input
              id="client-email"
              type="email"
              name="email"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
              New password
            </label>
            <input
              id="new-password"
              type="password"
              name="new-password"
              autoComplete="new-password"
              placeholder="New password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              autoFocus
              className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm password
            </label>
            <input
              id="confirm-password"
              type="password"
              name="password-confirmation"
              autoComplete="new-password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700 disabled:opacity-60"
          >
            {loading ? 'Creating account...' : 'Create Password & Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
